import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, FileText, Mail, Plus, RefreshCw, Save, Settings, Shield, Trash2, Upload, UserCheck, Users, X } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { functions, storage } from '../services/firebase';
import { useAuthStore } from '../stores/authStore';

const ROLE_OPTIONS = [
  { value: 'student', label: 'Learner' },
  { value: 'teacher', label: 'Teacher' },
  { value: 'tutor', label: 'Tutor' },
  { value: 'official', label: 'Official' },
  { value: 'admin', label: 'Admin' },
];

const formatDate = (value) => {
  if (!value) return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not available' : date.toLocaleString();
};

const createUserFormState = (user) => ({
  role: user?.role || 'student',
  isApproved: user?.isApproved === true,
  isAdmin: user?.role === 'admin' || user?.isAdmin === true,
});

const hasPendingSubscriptionRequest = (user) =>
  Boolean(
    user?.subscriptionRequest?.planId &&
    user.subscriptionRequest.planId !== 'free' &&
    user.subscriptionRequest.status === 'pending'
  );

const getSubscriptionLabel = (planId) => {
  if (planId === 'pro') return 'Pro';
  if (planId === 'basic') return 'Basic';
  return '30-day Trial';
};

const reduceStats = (users) => users.reduce((acc, entry) => {
  acc.total += 1;
  if (entry.isApproved) acc.approved += 1;
  else acc.pending += 1;
  acc.roles[entry.role] = (acc.roles[entry.role] || 0) + 1;
  return acc;
}, { total: 0, approved: 0, pending: 0, roles: {} });

const BUILT_IN_EMAIL_TEMPLATE_OPTIONS = [
  { key: 'userSignupConfirmation', label: 'User signup confirmation', recipientType: 'user' },
  { key: 'adminNewSignupAlert', label: 'Admin signup alert', recipientType: 'admin' },
  { key: 'userApproval', label: 'User approval notice', recipientType: 'user' },
  { key: 'adminSubscriptionRequest', label: 'Admin subscription request alert', recipientType: 'admin' },
  { key: 'userSubscriptionApproved', label: 'User subscription approved', recipientType: 'user' },
  { key: 'userSubscriptionDeclined', label: 'User subscription declined', recipientType: 'user' },
];
const isBuiltInEmailTemplateKey = (templateKey) => BUILT_IN_EMAIL_TEMPLATE_OPTIONS.some((option) => option.key === templateKey);
const isCustomEmailTemplateKey = (templateKey) => {
  const key = String(templateKey || '').trim();
  return Boolean(key) && !isBuiltInEmailTemplateKey(key);
};
const buildCustomTemplateKey = () => `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const DEFAULT_WRAPPED_EMAIL_DESIGN = {
  pageBackground: '#f8fafc',
  cardBackground: '#ffffff',
  borderColor: '#e2e8f0',
  borderRadius: '20px',
  fontFamily: 'Arial, sans-serif',
  textColor: '#0f172a',
  bodyTextColor: '#334155',
  mutedTextColor: '#64748b',
  headerBackground: 'linear-gradient(135deg,#1d4ed8 0%,#0f172a 100%)',
  headerTextColor: '#ffffff',
  buttonBackground: '#4e46e5',
  buttonTextColor: '#ffffff',
};

const buildWrappedEmailHtmlTemplate = (template = {}) => {
  const design = { ...DEFAULT_WRAPPED_EMAIL_DESIGN, ...(template.design || {}) };
  const appName = template.subject?.includes('{{appName}}') ? '{{appName}}' : '{{appName}}';
  const eyebrow = template.eyebrow || appName;
  const footerText = template.footerText || 'Sent by {{appName}}';
  return `
<div style="background:${design.pageBackground};padding:32px 16px;font-family:${design.fontFamily};color:${design.textColor}">
  <div style="max-width:640px;margin:0 auto;background:${design.cardBackground};border:1px solid ${design.borderColor};border-radius:${design.borderRadius};overflow:hidden">
    <div style="padding:28px 28px 20px;background:${design.headerBackground};color:${design.headerTextColor}">
      <div style="font-size:12px;letter-spacing:0.16em;text-transform:uppercase;opacity:0.8">${eyebrow}</div>
      <h1 style="margin:14px 0 0;font-size:28px;line-height:1.2">${template.title || ''}</h1>
    </div>
    <div style="padding:28px">
      <p style="margin:0 0 16px;font-size:16px;line-height:1.6">${template.intro || ''}</p>
      <div style="font-size:15px;line-height:1.7;color:${design.bodyTextColor}">${template.bodyHtml || ''}</div>
      ${template.actionLabel && template.actionUrl ? `<div style="margin-top:24px"><a href="${template.actionUrl}" style="display:inline-block;border-radius:999px;background:${design.buttonBackground};color:${design.buttonTextColor};text-decoration:none;padding:12px 18px;font-weight:600">${template.actionLabel}</a></div>` : ''}
      <p style="margin:24px 0 0;font-size:13px;line-height:1.6;color:${design.mutedTextColor}">${footerText}</p>
    </div>
  </div>
</div>`.trim();
};

const DEFAULT_EMAIL_TEMPLATES = {
  userSignupConfirmation: {
    enabled: true,
    recipientType: 'user',
    htmlMode: 'wrapped',
    design: DEFAULT_WRAPPED_EMAIL_DESIGN,
    subject: 'Welcome to {{appName}}',
    eyebrow: 'Account Created',
    title: 'Your signup is in',
    intro: 'Hi {{displayName}}, your {{appName}} account has been created.',
    bodyHtml: '<p style="margin:0 0 14px">Your account is currently pending admin review.</p><p style="margin:0">You will receive another email as soon as your access is approved.</p>',
    fullHtml: '',
    textBody: 'Your account is currently pending admin review. You will receive another email as soon as your access is approved.',
    actionLabel: 'Open {{appName}}',
    actionUrl: '{{appUrl}}',
    footerText: 'Questions? Reply to this email or contact {{supportEmail}}.',
  },
  adminNewSignupAlert: {
    enabled: true,
    recipientType: 'admin',
    htmlMode: 'wrapped',
    design: DEFAULT_WRAPPED_EMAIL_DESIGN,
    subject: 'New signup awaiting approval: {{displayName}}',
    eyebrow: 'Admin Alert',
    title: 'A new user is waiting for approval',
    intro: '{{displayName}} signed up for {{appName}}.',
    bodyHtml: '<p style="margin:0 0 10px"><strong>Email:</strong> {{userEmail}}</p><p style="margin:0 0 10px"><strong>Role:</strong> {{userRole}}</p><p style="margin:0"><strong>Institution:</strong> {{institutionName}}</p>',
    fullHtml: '',
    textBody: 'Email: {{userEmail}}\nRole: {{userRole}}\nInstitution: {{institutionName}}',
    actionLabel: 'Review Approvals',
    actionUrl: '{{adminUrl}}',
    footerText: 'Admin notification from {{appName}}.',
  },
  userApproval: {
    enabled: true,
    recipientType: 'user',
    htmlMode: 'wrapped',
    design: DEFAULT_WRAPPED_EMAIL_DESIGN,
    subject: '{{appName}} account approved',
    eyebrow: 'Access Approved',
    title: 'Your account is approved',
    intro: 'Hi {{displayName}}, your {{appName}} account is now active.',
    bodyHtml: '<p style="margin:0 0 14px">You can now sign in and use the platform.</p><p style="margin:0">If you requested a paid subscription, continue from the pricing page once you are signed in.</p>',
    fullHtml: '',
    textBody: 'You can now sign in and use the platform. If you requested a paid subscription, continue from the pricing page once you are signed in.',
    actionLabel: 'Open {{appName}}',
    actionUrl: '{{appUrl}}',
    footerText: 'Need help? Contact {{supportEmail}}.',
  },
  adminSubscriptionRequest: {
    enabled: true,
    recipientType: 'admin',
    htmlMode: 'wrapped',
    design: DEFAULT_WRAPPED_EMAIL_DESIGN,
    subject: 'Subscription request: {{planLabel}} - {{displayName}}',
    eyebrow: 'Subscription Request',
    title: 'A paid plan needs review',
    intro: '{{displayName}} submitted a {{planLabel}} subscription request.',
    bodyHtml: '<p style="margin:0 0 10px"><strong>Name:</strong> {{displayName}}</p><p style="margin:0 0 10px"><strong>Email:</strong> {{userEmail}}</p><p style="margin:0 0 10px"><strong>Role:</strong> {{userRole}}</p><p style="margin:0 0 10px"><strong>Institution:</strong> {{institutionName}}</p><p style="margin:0 0 10px"><strong>Plan:</strong> {{planLabel}}</p><p style="margin:0 0 10px"><strong>Amount:</strong> {{currencyAmount}}</p><p style="margin:0"><strong>User ID:</strong> {{userId}}</p>',
    fullHtml: '',
    textBody: 'Name: {{displayName}}\nEmail: {{userEmail}}\nRole: {{userRole}}\nInstitution: {{institutionName}}\nPlan: {{planLabel}}\nAmount: {{currencyAmount}}\nUser ID: {{userId}}',
    actionLabel: 'Review Request',
    actionUrl: '{{adminUrl}}',
    footerText: 'Admin notification from {{appName}}.',
  },
  userSubscriptionApproved: {
    enabled: true,
    recipientType: 'user',
    htmlMode: 'wrapped',
    design: DEFAULT_WRAPPED_EMAIL_DESIGN,
    subject: '{{planLabel}} plan approved',
    eyebrow: 'Subscription Approved',
    title: '{{planLabel}} is now active',
    intro: 'Hi {{displayName}}, your {{planLabel}} plan request has been approved.',
    bodyHtml: '<p style="margin:0 0 14px">Your paid subscription is now active on your account.</p><p style="margin:0">You can continue in {{appName}} and start using the unlocked features immediately.</p>',
    fullHtml: '',
    textBody: 'Your paid subscription is now active on your account. You can continue in {{appName}} and start using the unlocked features immediately.',
    actionLabel: 'Open Pricing',
    actionUrl: '{{pricingUrl}}',
    footerText: 'Questions about billing or access? Contact {{supportEmail}}.',
  },
  userSubscriptionDeclined: {
    enabled: true,
    recipientType: 'user',
    htmlMode: 'wrapped',
    design: DEFAULT_WRAPPED_EMAIL_DESIGN,
    subject: '{{planLabel}} plan request declined',
    eyebrow: 'Subscription Update',
    title: 'Your plan request was declined',
    intro: 'Hi {{displayName}}, your paid plan request was reviewed by the {{appName}} admin team.',
    bodyHtml: '<p style="margin:0 0 14px">The request was not approved at this stage.</p><p style="margin:0">If you need help or want to retry, contact support or open the pricing page for the latest options.</p>',
    fullHtml: '',
    textBody: 'The request was not approved at this stage. If you need help or want to retry, contact support or open the pricing page for the latest options.',
    actionLabel: 'Open Pricing',
    actionUrl: '{{pricingUrl}}',
    footerText: 'Need clarification? Contact {{supportEmail}}.',
  },
};
const DEFAULT_CUSTOM_EMAIL_TEMPLATE = {
  name: 'Custom template',
  isCustom: true,
  enabled: true,
  recipientType: 'user',
  htmlMode: 'wrapped',
  design: DEFAULT_WRAPPED_EMAIL_DESIGN,
  subject: '{{appName}} update for {{displayName}}',
  eyebrow: 'Custom Message',
  title: 'A message from {{appName}}',
  intro: 'Hi {{displayName}}, here is an update from the {{appName}} team.',
  bodyHtml: '<p style="margin:0 0 14px">Use this template for outreach, onboarding, or follow-up emails that you want to send manually.</p><p style="margin:0">Edit the content, design, and calls to action in Admin before sending.</p>',
  fullHtml: '',
  textBody: 'Use this template for outreach, onboarding, or follow-up emails that you want to send manually. Edit the content, design, and calls to action in Admin before sending.',
  actionLabel: 'Open {{appName}}',
  actionUrl: '{{appUrl}}',
  footerText: 'Questions? Reply to this email or contact {{supportEmail}}.',
};
const createDefaultCustomEmailTemplate = (name = 'Custom template') => ({
  ...DEFAULT_CUSTOM_EMAIL_TEMPLATE,
  name,
  design: { ...DEFAULT_WRAPPED_EMAIL_DESIGN },
});
const getDefaultTemplateByKey = (templateKey) => (
  isBuiltInEmailTemplateKey(templateKey) ?
    DEFAULT_EMAIL_TEMPLATES[templateKey] :
    createDefaultCustomEmailTemplate()
);
const normalizeTemplateEntryForState = (templateKey, template = {}, recipientType = '') => ({
  ...getDefaultTemplateByKey(templateKey),
  ...template,
  name: isBuiltInEmailTemplateKey(templateKey) ? '' : (template?.name || DEFAULT_CUSTOM_EMAIL_TEMPLATE.name),
  isCustom: isCustomEmailTemplateKey(templateKey),
  recipientType: recipientType || template?.recipientType || getDefaultTemplateByKey(templateKey).recipientType || 'user',
  design: {
    ...DEFAULT_WRAPPED_EMAIL_DESIGN,
    ...(template?.design || {}),
  },
});
const EMAIL_TEMPLATE_VARIABLES = [
  '{{appName}}',
  '{{displayName}}',
  '{{firstName}}',
  '{{lastName}}',
  '{{userEmail}}',
  '{{userRole}}',
  '{{institutionName}}',
  '{{school}}',
  '{{project}}',
  '{{planLabel}}',
  '{{currencyAmount}}',
  '{{supportEmail}}',
  '{{replyTo}}',
  '{{baseUrl}}',
  '{{appUrl}}',
  '{{pricingUrl}}',
  '{{adminUrl}}',
  '{{userId}}',
];

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, userData, isAdmin: isAdminUser } = useAuthStore();
  const sourceFileInputRef = useRef(null);

  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [activeUserActionId, setActiveUserActionId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [masterPrompt, setMasterPrompt] = useState('');
  const [modelNoFiles, setModelNoFiles] = useState('gemini-3.1-flash-lite-preview');
  const [modelWithFiles, setModelWithFiles] = useState('gemini-2.5-flash');
  const [maxSourceFiles, setMaxSourceFiles] = useState(8);
  const [allowedSourceMimeTypesText, setAllowedSourceMimeTypesText] = useState('');
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [isUploadingSourceFiles, setIsUploadingSourceFiles] = useState(false);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(false);
  const [emailProvider, setEmailProvider] = useState('zeptomail');
  const [emailAppName, setEmailAppName] = useState('Sigma Classroom');
  const [emailBaseUrl, setEmailBaseUrl] = useState('https://sawbona-app.web.app');
  const [supportEmail, setSupportEmail] = useState('');
  const [replyToEmail, setReplyToEmail] = useState('');
  const [adminRecipientsText, setAdminRecipientsText] = useState('');
  const [emailTemplates, setEmailTemplates] = useState(DEFAULT_EMAIL_TEMPLATES);
  const [selectedEmailTemplateKey, setSelectedEmailTemplateKey] = useState(BUILT_IN_EMAIL_TEMPLATE_OPTIONS[0].key);
  const [emailMainSection, setEmailMainSection] = useState('settings');
  const [manualEmailTemplateKey, setManualEmailTemplateKey] = useState(BUILT_IN_EMAIL_TEMPLATE_OPTIONS[0].key);
  const [manualEmailTargetMode, setManualEmailTargetMode] = useState('existing');
  const [manualEmailUserIds, setManualEmailUserIds] = useState([]);
  const [manualProspectiveEmails, setManualProspectiveEmails] = useState('');
  const [manualEmailPrefillUserId, setManualEmailPrefillUserId] = useState('');
  const [manualEmailAttachmentDocumentId, setManualEmailAttachmentDocumentId] = useState('');
  const [manualEmailAttachmentDocuments, setManualEmailAttachmentDocuments] = useState([]);
  const [manualEmailUploadedAttachment, setManualEmailUploadedAttachment] = useState(null);
  const [isUploadingManualEmailAttachment, setIsUploadingManualEmailAttachment] = useState(false);
  const [isLoadingEmailAttachmentDocuments, setIsLoadingEmailAttachmentDocuments] = useState(false);
  const [isManualEmailSending, setIsManualEmailSending] = useState(false);
  const [manualEmailStatus, setManualEmailStatus] = useState('');
  const [isUploadingEmailTemplateImage, setIsUploadingEmailTemplateImage] = useState(false);
  const [emailTemplateImageUrl, setEmailTemplateImageUrl] = useState('');
  const [emailTemplateImageStatus, setEmailTemplateImageStatus] = useState('');

  const [activeSection, setActiveSection] = useState('approvals');
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminStats, setAdminStats] = useState({ total: 0, approved: 0, pending: 0, roles: {} });
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [approvalFilter, setApprovalFilter] = useState('all');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userForm, setUserForm] = useState(createUserFormState(null));

  const isAdmin = isAdminUser() || String(userData?.userRole || '').trim().toLowerCase() === 'admin';
  const sections = useMemo(() => ([
    { id: 'approvals', label: 'Approvals', icon: UserCheck },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'emails', label: 'Emails', icon: Mail },
  ]), []);

  const loadSettings = useCallback(async () => {
    setIsLoadingSettings(true);
    setError('');
    try {
      const getAdminSettings = httpsCallable(functions, 'getAdminSettings');
      const result = await getAdminSettings();
      const settings = result?.data?.contentCreator || {};
      setMasterPrompt(settings.masterPrompt || '');
      setModelNoFiles(settings.modelNoFiles || 'gemini-3.1-flash-lite-preview');
      setModelWithFiles(settings.modelWithFiles || 'gemini-2.5-flash');
      setMaxSourceFiles(Number(settings.maxSourceFiles || 8));
      setAllowedSourceMimeTypesText(Array.isArray(settings.allowedSourceMimeTypes) ? settings.allowedSourceMimeTypes.join('\n') : '');
      const nextProjects = Array.isArray(settings.projects) ? settings.projects : [];
      setProjects(nextProjects);
      setSelectedProjectId((current) => current && nextProjects.some((p) => p.id === current) ? current : (nextProjects[0]?.id || ''));
    } catch (err) {
      setError(err?.message || 'Failed to load admin settings.');
    } finally {
      setIsLoadingSettings(false);
    }
  }, []);

  const loadEmailSettings = useCallback(async () => {
    setIsLoadingSettings(true);
    setError('');
    try {
      const getAdminEmailSettingsConfig = httpsCallable(functions, 'getAdminEmailSettingsConfig');
      const result = await getAdminEmailSettingsConfig();
      const emailSettings = result?.data?.emails || {};
      setEmailNotificationsEnabled(emailSettings.enabled === true);
      setEmailProvider(emailSettings.provider === 'gmail' ? 'gmail' : 'zeptomail');
      setEmailAppName(emailSettings.appName || 'Sigma Classroom');
      setEmailBaseUrl(emailSettings.baseUrl || 'https://sawbona-app.web.app');
      setSupportEmail(emailSettings.supportEmail || '');
      setReplyToEmail(emailSettings.replyTo || '');
      setAdminRecipientsText(
        Array.isArray(emailSettings.adminRecipients) ?
          emailSettings.adminRecipients
            .map((recipient) => recipient?.name ? `${recipient.name} <${recipient.address}>` : recipient.address)
            .join('\n') :
          ''
      );
      const builtInTemplates = Object.fromEntries(
        BUILT_IN_EMAIL_TEMPLATE_OPTIONS.map(({ key, recipientType }) => ([
          key,
          normalizeTemplateEntryForState(key, emailSettings.templates?.[key] || {}, recipientType),
        ]))
      );
      const customTemplates = Object.fromEntries(
        Object.entries(emailSettings.templates || {})
          .filter(([key]) => isCustomEmailTemplateKey(key))
          .map(([key, value]) => [key, normalizeTemplateEntryForState(key, value, value?.recipientType || 'user')])
      );
      const nextTemplates = {
        ...builtInTemplates,
        ...customTemplates,
      };
      setEmailTemplates(nextTemplates);
      setSelectedEmailTemplateKey((current) => nextTemplates[current] ? current : BUILT_IN_EMAIL_TEMPLATE_OPTIONS[0].key);
    } catch (err) {
      setError(err?.message || 'Failed to load email settings.');
    } finally {
      setIsLoadingSettings(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    setError('');
    try {
      const listAdminUsers = httpsCallable(functions, 'listAdminUsers');
      const result = await listAdminUsers({ limit: 400 });
      const nextUsers = Array.isArray(result?.data?.users) ? result.data.users : [];
      setAdminUsers(nextUsers);
      setAdminStats(result?.data?.stats || reduceStats(nextUsers));
      setSelectedUserId((current) => current && nextUsers.some((entry) => entry.id === current) ? current : (nextUsers[0]?.id || ''));
    } catch (err) {
      setError(err?.message || 'Failed to load users.');
    } finally {
      setIsLoadingUsers(false);
    }
  }, []);

  const loadEmailAttachmentDocuments = useCallback(async () => {
    setIsLoadingEmailAttachmentDocuments(true);
    try {
      const listAdminEmailAttachmentDocuments = httpsCallable(functions, 'listAdminEmailAttachmentDocuments');
      const result = await listAdminEmailAttachmentDocuments({ limit: 200 });
      setManualEmailAttachmentDocuments(Array.isArray(result?.data?.documents) ? result.data.documents : []);
    } catch (err) {
      console.error('Failed to load email attachment documents', err);
      setManualEmailAttachmentDocuments([]);
    } finally {
      setIsLoadingEmailAttachmentDocuments(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    loadSettings();
    loadEmailSettings();
    loadUsers();
    loadEmailAttachmentDocuments();
  }, [isAdmin, loadEmailAttachmentDocuments, loadEmailSettings, loadSettings, loadUsers]);

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === selectedProjectId) || null,
    [projects, selectedProjectId]
  );
  const emailTemplateOptions = useMemo(() => {
    const customOptions = Object.entries(emailTemplates)
      .filter(([key]) => isCustomEmailTemplateKey(key))
      .map(([key, template]) => ({
        key,
        label: String(template?.name || 'Custom template').trim() || 'Custom template',
        recipientType: template?.recipientType || 'user',
        isCustom: true,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    return [
      ...BUILT_IN_EMAIL_TEMPLATE_OPTIONS.map((option) => ({ ...option, isCustom: false })),
      ...customOptions,
    ];
  }, [emailTemplates]);
  const selectedEmailTemplate = useMemo(
    () => emailTemplates[selectedEmailTemplateKey] || getDefaultTemplateByKey(selectedEmailTemplateKey) || null,
    [emailTemplates, selectedEmailTemplateKey]
  );
  const manualSendTemplateOptions = useMemo(
    () => emailTemplateOptions.filter((option) => (emailTemplates[option.key]?.recipientType || option.recipientType) === 'user'),
    [emailTemplateOptions, emailTemplates]
  );
  const manualEmailUsers = useMemo(
    () => adminUsers.filter((entry) => manualEmailUserIds.includes(entry.id)),
    [adminUsers, manualEmailUserIds]
  );
  const manualPrefillUser = useMemo(
    () => adminUsers.find((entry) => entry.id === manualEmailPrefillUserId) || null,
    [adminUsers, manualEmailPrefillUserId]
  );

  const filteredUsers = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    return adminUsers.filter((entry) => {
      const matchesSearch = !search || [
        entry.displayName,
        entry.email,
        entry.institutionName,
        entry.school,
        entry.project,
      ].some((value) => String(value || '').toLowerCase().includes(search));
      const matchesRole = roleFilter === 'all' || entry.role === roleFilter;
      const matchesApproval = approvalFilter === 'all' || (approvalFilter === 'approved' ? entry.isApproved : !entry.isApproved);
      return matchesSearch && matchesRole && matchesApproval;
    });
  }, [adminUsers, approvalFilter, roleFilter, searchTerm]);

  const pendingUsers = useMemo(() => adminUsers.filter((entry) => !entry.isApproved), [adminUsers]);
  const pendingSubscriptionUsers = useMemo(
    () => adminUsers.filter((entry) => hasPendingSubscriptionRequest(entry)),
    [adminUsers]
  );
  const selectedUser = useMemo(() => adminUsers.find((entry) => entry.id === selectedUserId) || null, [adminUsers, selectedUserId]);

  useEffect(() => {
    setUserForm(createUserFormState(selectedUser));
  }, [selectedUser]);

  useEffect(() => {
    setManualEmailTemplateKey((current) => manualSendTemplateOptions.some((option) => option.key === current) ? current : (manualSendTemplateOptions[0]?.key || ''));
  }, [manualSendTemplateOptions]);

  useEffect(() => {
    setManualEmailUserIds((current) => {
      const validIds = current.filter((id) => adminUsers.some((entry) => entry.id === id));
      return validIds.length ? validIds : (adminUsers[0]?.id ? [adminUsers[0].id] : []);
    });
  }, [adminUsers]);

  useEffect(() => {
    setManualEmailAttachmentDocumentId((current) => (
      current && manualEmailAttachmentDocuments.some((entry) => entry.id === current) ? current : ''
    ));
  }, [manualEmailAttachmentDocuments]);

  useEffect(() => {
    setManualEmailPrefillUserId((current) => current && adminUsers.some((entry) => entry.id === current) ? current : '');
  }, [adminUsers]);

  const updateUserByAdmin = useCallback(async (targetUserId, updates, successMessage) => {
    if (!targetUserId) return;
    setActiveUserActionId(targetUserId);
    setIsSavingUser(true);
    setError('');
    setSuccess('');
    try {
      const updateAdminUser = httpsCallable(functions, 'updateAdminUser');
      const result = await updateAdminUser({ targetUserId, updates });
      const updatedUser = result?.data?.user;
      if (updatedUser) {
        setAdminUsers((current) => {
          const nextUsers = current.map((entry) => entry.id === updatedUser.id ? updatedUser : entry);
          setAdminStats(reduceStats(nextUsers));
          return nextUsers;
        });
        setUserForm(createUserFormState(updatedUser));
      }
      setSuccess(successMessage);
    } catch (err) {
      setError(err?.message || 'Failed to update user.');
    } finally {
      setActiveUserActionId('');
      setIsSavingUser(false);
    }
  }, []);

  const updateProjectById = useCallback((projectId, updater) => {
    setProjects((current) => current.map((project) => project.id === projectId ? updater(project) : project));
  }, []);

  const handleAddProject = useCallback(() => {
    const nextId = `project-${Date.now()}`;
    const nextProject = { id: nextId, name: 'New Project', description: '', instructions: '', contentTypes: [], sourceFiles: [] };
    setProjects((current) => [...current, nextProject]);
    setSelectedProjectId(nextId);
  }, []);

  const handleRemoveProject = useCallback((projectId) => {
    setProjects((current) => {
      const filtered = current.filter((project) => project.id !== projectId);
      if (selectedProjectId === projectId) setSelectedProjectId(filtered[0]?.id || '');
      return filtered;
    });
  }, [selectedProjectId]);

  const handleProjectFieldChange = useCallback((projectId, field, value) => {
    updateProjectById(projectId, (project) => ({ ...project, [field]: value }));
  }, [updateProjectById]);

  const handleProjectContentTypesChange = useCallback((projectId, rawValue) => {
    const contentTypes = rawValue.split(/\r?\n|,/g).map((value) => value.trim().toLowerCase()).filter(Boolean);
    updateProjectById(projectId, (project) => ({ ...project, contentTypes }));
  }, [updateProjectById]);

  const handleOpenSourceFilePicker = useCallback(() => {
    sourceFileInputRef.current?.click();
  }, []);

  const handleSourceFilesSelected = useCallback(async (event) => {
    const files = Array.from(event?.target?.files || []);
    if (!files.length) return;
    if (!selectedProject) {
      setError('Select a project first.');
      event.target.value = '';
      return;
    }

    setError('');
    setSuccess('');
    const allowedMimeTypes = allowedSourceMimeTypesText.split(/\r?\n|,/g).map((value) => value.trim().toLowerCase()).filter(Boolean);
    if ((selectedProject.sourceFiles?.length || 0) + files.length > Number(maxSourceFiles || 0)) {
      setError(`Project "${selectedProject.name}" allows a maximum of ${maxSourceFiles} source files.`);
      event.target.value = '';
      return;
    }

    const invalidFile = files.find((file) => !allowedMimeTypes.includes(String(file.type || '').toLowerCase()));
    if (invalidFile) {
      setError(`"${invalidFile.name}" has unsupported type "${invalidFile.type || 'unknown'}".`);
      event.target.value = '';
      return;
    }

    setIsUploadingSourceFiles(true);
    try {
      const uploaded = [];
      for (const file of files) {
        if (!user?.uid) throw new Error('No authenticated user.');
        const safeName = file.name.replace(/[^\w.-]+/g, '_');
        const uniqueName = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeName}`;
        const path = `content-creator-admin-sources/${user.uid}/${uniqueName}`;
        await uploadBytes(storageRef(storage, path), file, { contentType: file.type || 'application/octet-stream' });
        uploaded.push({ name: file.name, path, mimeType: file.type || 'application/octet-stream', size: file.size || 0 });
      }
      updateProjectById(selectedProject.id, (project) => ({ ...project, sourceFiles: [...(project.sourceFiles || []), ...uploaded] }));
    } catch (err) {
      setError(err?.message || 'Failed to upload admin source files.');
    } finally {
      setIsUploadingSourceFiles(false);
      event.target.value = '';
    }
  }, [allowedSourceMimeTypesText, maxSourceFiles, selectedProject, updateProjectById, user?.uid]);

  const handleRemoveSourceFile = useCallback((projectId, path) => {
    updateProjectById(projectId, (project) => ({
      ...project,
      sourceFiles: (project.sourceFiles || []).filter((item) => item.path !== path),
    }));
  }, [updateProjectById]);

  const updateEmailTemplate = useCallback((templateKey, updater) => {
    setEmailTemplates((current) => ({
      ...current,
      [templateKey]: updater(current[templateKey] || getDefaultTemplateByKey(templateKey)),
    }));
  }, []);

  const handleAddCustomEmailTemplate = useCallback(() => {
    const templateKey = buildCustomTemplateKey();
    setEmailTemplates((current) => ({
      ...current,
      [templateKey]: createDefaultCustomEmailTemplate(`Custom template ${Object.keys(current).filter((key) => isCustomEmailTemplateKey(key)).length + 1}`),
    }));
    setSelectedEmailTemplateKey(templateKey);
    setEmailMainSection('templates');
  }, []);

  const handleRemoveCustomEmailTemplate = useCallback((templateKey) => {
    if (!isCustomEmailTemplateKey(templateKey)) return;
    setEmailTemplates((current) => {
      const next = { ...current };
      delete next[templateKey];
      return next;
    });
    setSelectedEmailTemplateKey((current) => current === templateKey ? BUILT_IN_EMAIL_TEMPLATE_OPTIONS[0].key : current);
    setManualEmailTemplateKey((current) => current === templateKey ? (manualSendTemplateOptions[0]?.key || BUILT_IN_EMAIL_TEMPLATE_OPTIONS[0].key) : current);
  }, [manualSendTemplateOptions]);

  const handleEmailTemplateImageUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setEmailTemplateImageStatus('');
    setEmailTemplateImageUrl('');

    if (!user?.uid) {
      setEmailTemplateImageStatus('You must be signed in to upload an email image.');
      event.target.value = '';
      return;
    }

    if (!String(file.type || '').toLowerCase().startsWith('image/')) {
      setEmailTemplateImageStatus('Only image files can be uploaded for email templates.');
      event.target.value = '';
      return;
    }

    setIsUploadingEmailTemplateImage(true);
    try {
      const safeName = file.name.replace(/[^\w.-]+/g, '_');
      const uniqueName = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeName}`;
      const path = `email-template-images/${user.uid}/${uniqueName}`;
      const snapshot = await uploadBytes(storageRef(storage, path), file, {
        contentType: file.type || 'application/octet-stream',
      });
      const downloadUrl = await getDownloadURL(snapshot.ref);
      setEmailTemplateImageUrl(downloadUrl);
      setEmailTemplateImageStatus('Image uploaded. Copy the URL below into your HTML.');
    } catch (err) {
      setEmailTemplateImageStatus(err?.message || 'Failed to upload email template image.');
    } finally {
      setIsUploadingEmailTemplateImage(false);
      event.target.value = '';
    }
  }, [user?.uid]);

  const handleManualEmailAttachmentUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!user?.uid) {
      setManualEmailStatus('You must be signed in to upload an attachment.');
      event.target.value = '';
      return;
    }

    if (String(file.type || '').toLowerCase() !== 'application/pdf' && !String(file.name || '').toLowerCase().endsWith('.pdf')) {
      setManualEmailStatus('Only PDF attachments are supported.');
      event.target.value = '';
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      setManualEmailStatus('Attachment PDF must be 15 MB or smaller.');
      event.target.value = '';
      return;
    }

    setIsUploadingManualEmailAttachment(true);
    setManualEmailStatus('');
    try {
      const safeName = file.name.replace(/[^\w.-]+/g, '_');
      const uniqueName = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeName}`;
      const path = `email-template-attachments/${user.uid}/${uniqueName}`;
      const snapshot = await uploadBytes(storageRef(storage, path), file, {
        contentType: 'application/pdf',
      });
      const downloadUrl = await getDownloadURL(snapshot.ref);
      setManualEmailUploadedAttachment({
        name: file.name,
        url: downloadUrl,
        path,
        contentType: 'application/pdf',
        size: file.size,
      });
      setManualEmailAttachmentDocumentId('');
      setManualEmailStatus(`Attachment uploaded: ${file.name}`);
    } catch (err) {
      setManualEmailStatus(err?.message || 'Failed to upload attachment.');
    } finally {
      setIsUploadingManualEmailAttachment(false);
      event.target.value = '';
    }
  }, [user?.uid]);

  const handleManualEmailSend = useCallback(async () => {
    if (manualEmailTargetMode === 'existing' && manualEmailUserIds.length === 0) {
      setManualEmailStatus('Select at least one user first.');
      return;
    }
    if (manualEmailTargetMode === 'prospective' && !manualProspectiveEmails.trim()) {
      setManualEmailStatus('Enter at least one prospective email address.');
      return;
    }
    setIsManualEmailSending(true);
    setManualEmailStatus('');
    setError('');
    setSuccess('');
    try {
      const sendAdminEmailTemplate = httpsCallable(functions, 'sendAdminEmailTemplate');
      const result = await sendAdminEmailTemplate({
        templateKey: manualEmailTemplateKey,
        targetUserIds: manualEmailTargetMode === 'existing' ? manualEmailUserIds : [],
        targetUserId: manualEmailTargetMode === 'existing' ? (manualEmailUserIds[0] || '') : '',
        recipientEmails: manualEmailTargetMode === 'prospective' ? manualProspectiveEmails : '',
        prefillUserId: manualEmailTargetMode === 'prospective' ? manualEmailPrefillUserId : '',
        attachmentDocumentId: manualEmailAttachmentDocumentId,
        uploadedAttachment: manualEmailAttachmentDocumentId ? null : manualEmailUploadedAttachment,
      });
      const sentTo = Array.isArray(result?.data?.recipientEmails) && result.data.recipientEmails.length
        ? result.data.recipientEmails.join(', ')
        : (result?.data?.recipientEmail || manualEmailUsers[0]?.email || 'the selected users');
      const attachmentText = result?.data?.attachmentFileName ? ` with ${result.data.attachmentFileName} attached` : '';
      setManualEmailStatus(`Sent ${emailTemplateOptions.find((option) => option.key === manualEmailTemplateKey)?.label || 'email'} to ${sentTo}${attachmentText}.`);
    } catch (err) {
      setManualEmailStatus(err?.message || 'Failed to send manual email.');
    } finally {
      setIsManualEmailSending(false);
    }
  }, [emailTemplateOptions, manualEmailAttachmentDocumentId, manualEmailPrefillUserId, manualEmailTargetMode, manualEmailTemplateKey, manualEmailUploadedAttachment, manualEmailUserIds, manualEmailUsers, manualProspectiveEmails]);

  const handleSaveSettings = useCallback(async () => {
    setIsSavingSettings(true);
    setError('');
    setSuccess('');
    try {
      const updateAdminSettings = httpsCallable(functions, 'updateAdminSettings');
      await updateAdminSettings({
        contentCreator: {
          masterPrompt: masterPrompt.trim(),
          modelNoFiles: modelNoFiles.trim(),
          modelWithFiles: modelWithFiles.trim(),
          maxSourceFiles: Number(maxSourceFiles),
          allowedSourceMimeTypes: allowedSourceMimeTypesText.split(/\r?\n|,/g).map((value) => value.trim()).filter(Boolean),
          projects,
        },
        emails: {
          enabled: emailNotificationsEnabled,
          provider: emailProvider,
          appName: emailAppName.trim(),
          baseUrl: emailBaseUrl.trim(),
          supportEmail: supportEmail.trim(),
          replyTo: replyToEmail.trim(),
          adminRecipients: adminRecipientsText
            .split(/\r?\n|,/g)
            .map((value) => value.trim())
            .filter(Boolean)
            .map((value) => {
              const match = value.match(/^(.*?)<([^>]+)>$/);
              if (match) {
                return { name: match[1].trim(), address: match[2].trim() };
              }
              return { address: value };
            }),
          triggers: Object.fromEntries(
            Object.entries(emailTemplates).map(([key, template]) => [key, template?.enabled !== false])
          ),
          templates: emailTemplates,
        },
      });
      setSuccess('Admin settings saved.');
    } catch (err) {
      setError(err?.message || 'Failed to save admin settings.');
    } finally {
      setIsSavingSettings(false);
    }
  }, [
    adminRecipientsText,
    allowedSourceMimeTypesText,
    emailAppName,
    emailBaseUrl,
    emailNotificationsEnabled,
    emailProvider,
    emailTemplates,
    masterPrompt,
    maxSourceFiles,
    modelNoFiles,
    modelWithFiles,
    projects,
    replyToEmail,
    supportEmail,
  ]);

  const handleSaveEmailSettings = useCallback(async () => {
    setIsSavingSettings(true);
    setError('');
    setSuccess('');
    try {
      const updateAdminEmailSettings = httpsCallable(functions, 'updateAdminEmailSettings');
      await updateAdminEmailSettings({
        emails: {
          enabled: emailNotificationsEnabled,
          provider: emailProvider,
          appName: emailAppName.trim(),
          baseUrl: emailBaseUrl.trim(),
          supportEmail: supportEmail.trim(),
          replyTo: replyToEmail.trim(),
          adminRecipients: adminRecipientsText
            .split(/\r?\n|,/g)
            .map((value) => value.trim())
            .filter(Boolean)
            .map((value) => {
              const match = value.match(/^(.*?)<([^>]+)>$/);
              if (match) {
                return { name: match[1].trim(), address: match[2].trim() };
              }
              return { address: value };
            }),
          triggers: Object.fromEntries(
            Object.entries(emailTemplates).map(([key, template]) => [key, template?.enabled !== false])
          ),
          templates: emailTemplates,
        },
      });
      setSuccess('Email settings saved.');
      await loadEmailSettings();
    } catch (err) {
      console.error('Failed to save email settings', {
        error: err,
        templateKeys: Object.keys(emailTemplates || {}),
        selectedEmailTemplateKey,
      });
      setError(err?.message || 'Failed to save email settings.');
    } finally {
      setIsSavingSettings(false);
    }
  }, [
    adminRecipientsText,
    emailAppName,
    emailBaseUrl,
    emailNotificationsEnabled,
    emailProvider,
    emailTemplates,
    loadEmailSettings,
    replyToEmail,
    selectedEmailTemplateKey,
    supportEmail,
  ]);

  const handleSaveSelectedUser = useCallback(async () => {
    if (!selectedUser) return;
    await updateUserByAdmin(selectedUser.id, userForm, `Saved changes for ${selectedUser.displayName}.`);
  }, [selectedUser, updateUserByAdmin, userForm]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
        <div className="w-full max-w-md rounded-xl bg-white border border-red-200 p-6 text-center">
          <Shield className="w-8 h-8 mx-auto text-red-500 mb-3" />
          <h1 className="text-lg font-semibold text-gray-900">Admin Access Required</h1>
          <p className="text-sm text-gray-600 mt-2">You do not have permission to access this page.</p>
          <button type="button" onClick={() => navigate('/document/new')} className="mt-4 px-4 py-2 rounded-md bg-gray-900 text-white text-sm">Back To Editor</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6 gap-3">
          <button type="button" onClick={() => navigate('/document/new')} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50">
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex items-center gap-2">
            {(activeSection === 'approvals' || activeSection === 'users') && (
              <button type="button" onClick={loadUsers} disabled={isLoadingUsers} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60">
                <RefreshCw className={`w-4 h-4 ${isLoadingUsers ? 'animate-spin' : ''}`} />
                Refresh Users
              </button>
            )}
            {activeSection === 'settings' && (
              <button type="button" onClick={handleSaveSettings} disabled={isSavingSettings || isLoadingSettings} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-sigma-700 text-white text-sm font-medium hover:bg-sigma-800 disabled:opacity-60">
                <Save className="w-4 h-4" />
                {isSavingSettings ? 'Saving...' : 'Save Settings'}
              </button>
            )}
            {activeSection === 'emails' && (
              <button type="button" onClick={handleSaveEmailSettings} disabled={isSavingSettings || isLoadingSettings} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-sigma-700 text-white text-sm font-medium hover:bg-sigma-800 disabled:opacity-60">
                <Save className="w-4 h-4" />
                {isSavingSettings ? 'Saving...' : 'Save Emails'}
              </button>
            )}
            {activeSection === 'users' && selectedUser && (
              <button type="button" onClick={handleSaveSelectedUser} disabled={isSavingUser} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-sigma-700 text-white text-sm font-medium hover:bg-sigma-800 disabled:opacity-60">
                <Save className="w-4 h-4" />
                {isSavingUser && activeUserActionId === selectedUser.id ? 'Saving...' : 'Save User'}
              </button>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] min-h-[720px]">
            <aside className="border-r border-gray-200 bg-gray-50 p-3">
              <h1 className="text-sm font-semibold text-gray-900 px-2 py-2">Admin Area</h1>
              <div className="space-y-1 mt-2">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const current = section.id === activeSection;
                  return (
                    <button key={section.id} type="button" onClick={() => setActiveSection(section.id)} className={['w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors', current ? 'bg-sigma-700 text-white' : 'text-gray-700 hover:bg-gray-200'].join(' ')}>
                      <Icon className="w-4 h-4" />
                      {section.label}
                    </button>
                  );
                })}
              </div>
            </aside>

            <main className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4"><div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Users</div><div className="mt-2 text-2xl font-semibold text-gray-900">{adminStats.total || 0}</div></div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4"><div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Pending</div><div className="mt-2 text-2xl font-semibold text-amber-600">{adminStats.pending || 0}</div></div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4"><div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Approved</div><div className="mt-2 text-2xl font-semibold text-emerald-600">{adminStats.approved || 0}</div></div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4"><div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Plan Requests</div><div className="mt-2 text-2xl font-semibold text-sigma-700">{pendingSubscriptionUsers.length}</div></div>
              </div>

              {activeSection === 'approvals' && (
                <div className="space-y-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Pending Approvals</h2>
                    <p className="text-sm text-gray-500 mt-1">Review new accounts and paid-plan requests before granting access.</p>
                  </div>

                  {isLoadingUsers ? (
                    <div className="text-sm text-gray-600">Loading users...</div>
                  ) : pendingUsers.length === 0 ? (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">No pending users right now.</div>
                  ) : (
                    <div className="space-y-3">
                      {pendingUsers.map((entry) => (
                        <div key={entry.id} className="rounded-lg border border-gray-200 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-sm font-semibold text-gray-900">{entry.displayName}</h3>
                              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700">Pending</span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{entry.email || 'No email'}</p>
                            <p className="text-xs text-gray-500 mt-1">{entry.userRole} {entry.institutionName ? ` - ${entry.institutionName}` : ''}</p>
                            <p className="text-xs text-gray-500 mt-1">Created {formatDate(entry.createdAt)}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={() => { setSelectedUserId(entry.id); setActiveSection('users'); }} className="px-3 py-2 rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50">Review</button>
                            <button
                              type="button"
                              onClick={() => updateUserByAdmin(entry.id, { isApproved: true }, `Approved ${entry.displayName}.`)}
                              disabled={isSavingUser && activeUserActionId === entry.id}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                              {isSavingUser && activeUserActionId === entry.id ? 'Approving...' : 'Approve'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="pt-2">
                    <h3 className="text-sm font-semibold text-gray-900">Pending Subscription Requests</h3>
                    <p className="text-sm text-gray-500 mt-1">Approve paid plans before Basic or Pro access is assigned.</p>
                  </div>

                  {isLoadingUsers ? (
                    <div className="text-sm text-gray-600">Loading subscription requests...</div>
                  ) : pendingSubscriptionUsers.length === 0 ? (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">No paid-plan requests are waiting for approval.</div>
                  ) : (
                    <div className="space-y-3">
                      {pendingSubscriptionUsers.map((entry) => (
                        <div key={`${entry.id}-subscription-request`} className="rounded-lg border border-gray-200 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-sm font-semibold text-gray-900">{entry.displayName}</h3>
                              <span className="inline-flex items-center rounded-full bg-sigma-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-sigma-700">
                                {getSubscriptionLabel(entry.subscriptionRequest.planId)} Request
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{entry.email || 'No email'}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Current plan: {getSubscriptionLabel(entry.subscription?.planId)}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Requested {formatDate(entry.subscriptionRequest.requestedAt)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button type="button" onClick={() => { setSelectedUserId(entry.id); setActiveSection('users'); }} className="px-3 py-2 rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50">Review</button>
                            <button
                              type="button"
                              onClick={() => updateUserByAdmin(entry.id, { subscriptionRequestAction: 'approve' }, `Approved ${getSubscriptionLabel(entry.subscriptionRequest.planId)} for ${entry.displayName}.`)}
                              disabled={isSavingUser && activeUserActionId === entry.id}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                              {isSavingUser && activeUserActionId === entry.id ? 'Approving...' : 'Approve Plan'}
                            </button>
                            <button
                              type="button"
                              onClick={() => updateUserByAdmin(entry.id, { subscriptionRequestAction: 'decline' }, `Declined the subscription request for ${entry.displayName}.`)}
                              disabled={isSavingUser && activeUserActionId === entry.id}
                              className="px-3 py-2 rounded-md border border-red-300 bg-red-50 text-red-700 text-sm font-medium hover:bg-red-100 disabled:opacity-60"
                            >
                              Decline
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeSection === 'users' && (
                <div className="space-y-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">User Management</h2>
                    <p className="text-sm text-gray-500 mt-1">Search users, adjust role assignments, and control approval state.</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-[1.2fr_160px_160px] gap-3">
                    <input type="text" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search by name, email, school, project" className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                    <select value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300">
                      <option value="all">All roles</option>
                      {ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    <select value={approvalFilter} onChange={(event) => setApprovalFilter(event.target.value)} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300">
                      <option value="all">All approvals</option>
                      <option value="approved">Approved</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 min-h-[520px]">
                    <div className="rounded-lg border border-gray-200 overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 text-sm font-medium text-gray-700">{filteredUsers.length} users</div>
                      <div className="max-h-[560px] overflow-y-auto">
                        {isLoadingUsers ? (
                          <div className="p-4 text-sm text-gray-500">Loading users...</div>
                        ) : filteredUsers.length === 0 ? (
                          <div className="p-4 text-sm text-gray-500">No users match the current filters.</div>
                        ) : (
                          filteredUsers.map((entry) => {
                            const current = entry.id === selectedUserId;
                            return (
                              <button key={entry.id} type="button" onClick={() => setSelectedUserId(entry.id)} className={['w-full text-left px-4 py-3 border-b border-gray-100 transition-colors', current ? 'bg-sigma-50' : 'hover:bg-gray-50'].join(' ')}>
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-sm font-medium text-gray-900 truncate">{entry.displayName}</div>
                                  <div className="flex items-center gap-1.5">
                                    {hasPendingSubscriptionRequest(entry) ? (
                                      <span className="inline-flex items-center rounded-full bg-sigma-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-sigma-700">
                                        Plan
                                      </span>
                                    ) : null}
                                    <span className={['inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold uppercase tracking-wide', entry.isApproved ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'].join(' ')}>
                                      {entry.isApproved ? 'Approved' : 'Pending'}
                                    </span>
                                  </div>
                                </div>
                                <div className="text-xs text-gray-500 mt-1 truncate">{entry.email}</div>
                                <div className="text-xs text-gray-500 mt-1">{entry.userRole} {entry.institutionName ? ` - ${entry.institutionName}` : ''}</div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>

                    <div className="rounded-lg border border-gray-200 p-5">
                      {!selectedUser ? (
                        <div className="text-sm text-gray-500">Select a user to manage their account.</div>
                      ) : (
                        <div className="space-y-5">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">{selectedUser.displayName}</h3>
                              <p className="text-sm text-gray-500 mt-1">{selectedUser.email || 'No email'}</p>
                            </div>
                            <span className={['inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide', selectedUser.isApproved ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'].join(' ')}>{selectedUser.isApproved ? 'Approved' : 'Pending'}</span>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div className="rounded-md bg-gray-50 p-3">
                              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Institution</div>
                              <div className="mt-1 text-gray-900">{selectedUser.institutionName || 'Not set'}</div>
                              <div className="text-xs text-gray-500 mt-1">{selectedUser.institutionType || 'Type not set'}</div>
                            </div>
                            <div className="rounded-md bg-gray-50 p-3">
                              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Profile</div>
                              <div className="mt-1 text-gray-900">{selectedUser.profileComplete ? 'Complete' : 'Incomplete'}</div>
                              <div className="text-xs text-gray-500 mt-1">Updated {formatDate(selectedUser.updatedAt)}</div>
                            </div>
                          </div>

                          <div className="rounded-md border border-gray-200 p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Subscription</div>
                                <div className="mt-1 text-sm font-medium text-gray-900">
                                  Active: {getSubscriptionLabel(selectedUser.subscription?.planId)}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                  Status: {selectedUser.subscription?.status || 'active'}
                                </div>
                              </div>
                              {hasPendingSubscriptionRequest(selectedUser) ? (
                                <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-amber-700">
                                  Pending {getSubscriptionLabel(selectedUser.subscriptionRequest.planId)}
                                </span>
                              ) : null}
                            </div>

                            {hasPendingSubscriptionRequest(selectedUser) ? (
                              <div className="mt-4 flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={() => updateUserByAdmin(selectedUser.id, { subscriptionRequestAction: 'approve' }, `Approved ${getSubscriptionLabel(selectedUser.subscriptionRequest.planId)} for ${selectedUser.displayName}.`)}
                                  disabled={isSavingUser}
                                  className="px-3 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
                                >
                                  Approve Plan
                                </button>
                                <button
                                  type="button"
                                  onClick={() => updateUserByAdmin(selectedUser.id, { subscriptionRequestAction: 'decline' }, `Declined the subscription request for ${selectedUser.displayName}.`)}
                                  disabled={isSavingUser}
                                  className="px-3 py-2 rounded-md border border-red-300 bg-red-50 text-red-700 text-sm font-medium hover:bg-red-100 disabled:opacity-60"
                                >
                                  Decline Request
                                </button>
                              </div>
                            ) : (
                              <div className="mt-3 text-xs text-gray-500">
                                No paid-plan request is waiting for review.
                              </div>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                              <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Role</label>
                              <select
                                value={userForm.role}
                                onChange={(event) => {
                                  const nextRole = event.target.value;
                                  setUserForm((current) => ({ ...current, role: nextRole, isAdmin: nextRole === 'admin' ? true : current.isAdmin }));
                                }}
                                className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300"
                              >
                                {ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                              </select>
                            </div>
                            <div className="space-y-2">
                              <label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Access</label>
                              <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input type="checkbox" checked={userForm.isApproved} onChange={(event) => setUserForm((current) => ({ ...current, isApproved: event.target.checked }))} className="rounded border-gray-300 text-sigma-600 focus:ring-sigma-500" />
                                Approved account
                              </label>
                              <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                  type="checkbox"
                                  checked={userForm.isAdmin}
                                  onChange={(event) => {
                                    const nextIsAdmin = event.target.checked;
                                    setUserForm((current) => ({ ...current, isAdmin: nextIsAdmin, role: nextIsAdmin ? 'admin' : (current.role === 'admin' ? 'official' : current.role) }));
                                  }}
                                  className="rounded border-gray-300 text-sigma-600 focus:ring-sigma-500"
                                />
                                System admin
                              </label>
                            </div>
                          </div>

                          <div className="rounded-md border border-gray-200 p-4">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Quick Actions</div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <button type="button" onClick={() => updateUserByAdmin(selectedUser.id, { isApproved: true }, `Approved ${selectedUser.displayName}.`)} disabled={isSavingUser} className="px-3 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60">Approve</button>
                              <button type="button" onClick={() => updateUserByAdmin(selectedUser.id, { isApproved: false }, `Moved ${selectedUser.displayName} back to pending.`)} disabled={isSavingUser} className="px-3 py-2 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-sm font-medium hover:bg-amber-100 disabled:opacity-60">Mark Pending</button>
                              <button type="button" onClick={() => setUserForm(createUserFormState(selectedUser))} className="px-3 py-2 rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50">Reset Form</button>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-500">
                            <div>School: {selectedUser.school || 'Not set'}</div>
                            <div>Project: {selectedUser.project || 'Not set'}</div>
                            <div>Provider: {selectedUser.authProvider || 'Unknown'}</div>
                            <div>Created: {formatDate(selectedUser.createdAt)}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'settings' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Creator Settings</h2>
                    <p className="text-sm text-gray-500 mt-1">Master prompt and source-file search policy used by the document creator.</p>
                  </div>

                  {isLoadingSettings ? (
                    <div className="text-sm text-gray-600">Loading settings...</div>
                  ) : (
                    <>
                      <section className="space-y-2">
                        <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Master Prompt</h3>
                        <textarea value={masterPrompt} onChange={(event) => setMasterPrompt(event.target.value)} rows={14} placeholder="Global master prompt used by the creator." className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                      </section>

                      <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="space-y-1.5">
                          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Model (No Files)</label>
                          <input type="text" value={modelNoFiles} onChange={(event) => setModelNoFiles(event.target.value)} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Model (File Search)</label>
                          <input type="text" value={modelWithFiles} onChange={(event) => setModelWithFiles(event.target.value)} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Max Source Files</label>
                          <input type="number" min={1} max={20} value={maxSourceFiles} onChange={(event) => setMaxSourceFiles(event.target.value)} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                        </div>
                      </section>

                      <section className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Projects</h3>
                          <button type="button" onClick={handleAddProject} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-gray-300 bg-white text-xs font-medium text-gray-700 hover:bg-gray-100">
                            <Plus className="w-3.5 h-3.5" />
                            Add Project
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-3">
                          <div className="rounded-md border border-gray-200 bg-gray-50 p-2 space-y-1 max-h-64 overflow-y-auto">
                            {projects.map((project) => (
                              <button key={project.id} type="button" onClick={() => setSelectedProjectId(project.id)} className={['w-full text-left px-2 py-1.5 rounded text-xs', selectedProjectId === project.id ? 'bg-sigma-700 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'].join(' ')}>
                                <div className="font-medium truncate">{project.name}</div>
                                <div className="opacity-80 truncate">{project.id}</div>
                              </button>
                            ))}
                          </div>

                          <div>
                            {!selectedProject ? (
                              <div className="text-xs text-gray-500">Select or add a project.</div>
                            ) : (
                              <div className="space-y-2">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  <input type="text" value={selectedProject.id} readOnly className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm bg-gray-50 text-gray-500" />
                                  <input type="text" value={selectedProject.name} onChange={(event) => handleProjectFieldChange(selectedProject.id, 'name', event.target.value)} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                </div>
                                <textarea value={selectedProject.description || ''} onChange={(event) => handleProjectFieldChange(selectedProject.id, 'description', event.target.value)} rows={2} placeholder="Project description" className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                <textarea value={(selectedProject.contentTypes || []).join(', ')} onChange={(event) => handleProjectContentTypesChange(selectedProject.id, event.target.value)} rows={2} placeholder="notes, exam, worksheet" className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                <textarea value={selectedProject.instructions || ''} onChange={(event) => handleProjectFieldChange(selectedProject.id, 'instructions', event.target.value)} rows={4} placeholder="Project-specific instructions for the model." className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />

                                <div className="flex items-center gap-2">
                                  <input ref={sourceFileInputRef} type="file" multiple className="hidden" onChange={handleSourceFilesSelected} />
                                  <button type="button" onClick={handleOpenSourceFilePicker} disabled={isUploadingSourceFiles} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-60">
                                    <Upload className="w-4 h-4" />
                                    {isUploadingSourceFiles ? 'Uploading...' : 'Upload Project Files'}
                                  </button>
                                  <button type="button" onClick={() => handleRemoveProject(selectedProject.id)} className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-md border border-red-200 bg-red-50 text-xs font-medium text-red-700 hover:bg-red-100">
                                    <Trash2 className="w-3.5 h-3.5" />
                                    Delete Project
                                  </button>
                                </div>

                                {(selectedProject.sourceFiles || []).length > 0 ? (
                                  <div className="max-h-40 overflow-y-auto space-y-1.5 rounded-md border border-gray-200 bg-gray-50 p-2">
                                    {(selectedProject.sourceFiles || []).map((file) => (
                                      <div key={file.path} className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-2 py-1.5">
                                        <div className="min-w-0 flex items-center gap-2">
                                          <FileText className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                                          <div className="min-w-0">
                                            <div className="text-xs text-gray-700 truncate">{file.name}</div>
                                            <div className="text-[11px] text-gray-500">{file.mimeType}</div>
                                          </div>
                                        </div>
                                        <button type="button" onClick={() => handleRemoveSourceFile(selectedProject.id, file.path)} className="p-1 text-gray-400 hover:text-red-500" title="Remove source">
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-xs text-gray-500">No source files uploaded for this project.</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </section>

                      <section className="space-y-2">
                        <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Allowed Source Mime Types</h3>
                        <textarea value={allowedSourceMimeTypesText} onChange={(event) => setAllowedSourceMimeTypesText(event.target.value)} rows={6} placeholder={'application/pdf\ntext/plain\napplication/json'} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                        <p className="text-xs text-gray-500">One mime type per line or comma-separated.</p>
                      </section>
                    </>
                  )}
                </div>
              )}

              {activeSection === 'emails' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Emails</h2>
                    <p className="text-sm text-gray-500 mt-1">Manage delivery settings and Firestore-backed templates.</p>
                  </div>

                  {isLoadingSettings ? (
                    <div className="text-sm text-gray-600">Loading email settings...</div>
                  ) : (
                    <>
                      <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
                        <button
                          type="button"
                          onClick={() => setEmailMainSection('settings')}
                          className={['rounded-md px-4 py-2 text-sm font-medium transition-colors', emailMainSection === 'settings' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'].join(' ')}
                        >
                          Email Settings
                        </button>
                        <button
                          type="button"
                          onClick={() => setEmailMainSection('templates')}
                          className={['rounded-md px-4 py-2 text-sm font-medium transition-colors', emailMainSection === 'templates' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'].join(' ')}
                        >
                          Templates
                        </button>
                      </div>

                      {emailMainSection === 'settings' && (
                      <section className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="text-sm font-semibold text-gray-900">Delivery</h3>
                            <p className="text-xs text-gray-500 mt-1">This controls whether user and admin notification emails are sent from Cloud Functions.</p>
                          </div>
                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={emailNotificationsEnabled}
                              onChange={(event) => setEmailNotificationsEnabled(event.target.checked)}
                              className="rounded border-gray-300 text-sigma-600 focus:ring-sigma-500"
                            />
                            Enable email notifications
                          </label>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Email Provider</label>
                            <select value={emailProvider} onChange={(event) => setEmailProvider(event.target.value)} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300">
                              <option value="zeptomail">ZeptoMail</option>
                              <option value="gmail">Gmail App Password</option>
                            </select>
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">App Name</label>
                            <input type="text" value={emailAppName} onChange={(event) => setEmailAppName(event.target.value)} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Base URL</label>
                            <input type="url" value={emailBaseUrl} onChange={(event) => setEmailBaseUrl(event.target.value)} placeholder="https://sawbona-app.web.app" className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Support Email</label>
                            <input type="email" value={supportEmail} onChange={(event) => setSupportEmail(event.target.value)} placeholder="support@yourdomain.com" className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Reply-To Email</label>
                            <input type="email" value={replyToEmail} onChange={(event) => setReplyToEmail(event.target.value)} placeholder="hello@yourdomain.com" className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Admin Recipients</label>
                          <textarea
                            value={adminRecipientsText}
                            onChange={(event) => setAdminRecipientsText(event.target.value)}
                            rows={5}
                            placeholder={'admin@school.org\nJane Admin <jane@school.org>'}
                            className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300"
                          />
                          <p className="text-xs text-gray-500">One address per line. Use either `email@domain.com` or `Name &lt;email@domain.com&gt;`.</p>
                        </div>
                      </section>
                      )}

                      {emailMainSection === 'templates' && (
                      <section className="rounded-lg border border-gray-200 p-4 space-y-4">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">Templates</h3>
                          <p className="text-xs text-gray-500 mt-1">Templates are stored in Firestore. The function now only renders the selected template for each email type and sends it.</p>
                        </div>

                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={handleAddCustomEmailTemplate}
                            className="inline-flex items-center gap-2 rounded-md border border-sigma-200 bg-sigma-50 px-3 py-2 text-sm font-medium text-sigma-800 hover:bg-sigma-100"
                          >
                            <Plus className="h-4 w-4" />
                            Add Custom Template
                          </button>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
                          <div className="rounded-md border border-gray-200 overflow-hidden">
                            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 text-sm font-medium text-gray-700">Email Types</div>
                            <div className="divide-y divide-gray-100">
                              {emailTemplateOptions.map((option) => {
                                const current = option.key === selectedEmailTemplateKey;
                                const template = emailTemplates[option.key] || getDefaultTemplateByKey(option.key);
                                return (
                                  <button
                                    key={option.key}
                                    type="button"
                                    onClick={() => setSelectedEmailTemplateKey(option.key)}
                                    className={['w-full px-4 py-3 text-left transition-colors', current ? 'bg-sigma-50' : 'hover:bg-gray-50'].join(' ')}
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="text-sm font-medium text-gray-900">{option.label}</div>
                                      <span className={['inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold uppercase tracking-wide', template?.enabled !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'].join(' ')}>
                                        {template?.enabled !== false ? 'Enabled' : 'Disabled'}
                                      </span>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">{option.isCustom ? 'Custom template' : 'Built-in template'} • Recipient: {option.recipientType === 'admin' ? 'Admins' : 'User'}</div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          <div className="rounded-md border border-gray-200 p-4 space-y-4">
                            {!selectedEmailTemplate ? null : (
                              <>
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                  <div>
                                    <h4 className="text-sm font-semibold text-gray-900">
                                      {emailTemplateOptions.find((option) => option.key === selectedEmailTemplateKey)?.label || 'Template'}
                                    </h4>
                                    <p className="text-xs text-gray-500 mt-1">Configure the subject, wrapper fields, body content, and action used for this email type.</p>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    {isCustomEmailTemplateKey(selectedEmailTemplateKey) ? (
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveCustomEmailTemplate(selectedEmailTemplateKey)}
                                        className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-100"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                        Delete
                                      </button>
                                    ) : null}
                                    <label className="flex items-center gap-2 text-sm text-gray-700">
                                      <input
                                        type="checkbox"
                                        checked={selectedEmailTemplate.enabled !== false}
                                        onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, enabled: event.target.checked }))}
                                        className="rounded border-gray-300 text-sigma-600 focus:ring-sigma-500"
                                      />
                                      Enabled
                                    </label>
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {isCustomEmailTemplateKey(selectedEmailTemplateKey) ? (
                                    <div className="space-y-1.5 md:col-span-2">
                                      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Template Name</label>
                                      <input type="text" value={selectedEmailTemplate.name || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, name: event.target.value }))} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                    </div>
                                  ) : null}
                                  <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Subject</label>
                                    <input type="text" value={selectedEmailTemplate.subject || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, subject: event.target.value }))} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                  </div>
                                  <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Recipient Type</label>
                                    <input type="text" value={selectedEmailTemplate.recipientType === 'admin' ? 'Admins' : 'User'} readOnly className="w-full px-3 py-2 rounded-md border border-gray-300 bg-gray-50 text-sm text-gray-500" />
                                  </div>
                                  <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">HTML Mode</label>
                                    <select value={selectedEmailTemplate.htmlMode || 'wrapped'} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, htmlMode: event.target.value }))} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300">
                                      <option value="wrapped">Wrapped Layout</option>
                                      <option value="full">Full HTML</option>
                                    </select>
                                  </div>
                                  <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Eyebrow</label>
                                    <input type="text" value={selectedEmailTemplate.eyebrow || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, eyebrow: event.target.value }))} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                  </div>
                                  <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Title</label>
                                    <input type="text" value={selectedEmailTemplate.title || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, title: event.target.value }))} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                  </div>
                                </div>

                                {selectedEmailTemplate.htmlMode !== 'full' && (
                                  <>
                                    <div className="rounded-md border border-gray-200 bg-gray-50 p-4 space-y-3">
                                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Wrapped Design</div>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Page Background</label>
                                          <input type="text" value={selectedEmailTemplate.design?.pageBackground || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, design: { ...(current.design || DEFAULT_WRAPPED_EMAIL_DESIGN), pageBackground: event.target.value } }))} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                        </div>
                                        <div className="space-y-1.5">
                                          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Card Background</label>
                                          <input type="text" value={selectedEmailTemplate.design?.cardBackground || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, design: { ...(current.design || DEFAULT_WRAPPED_EMAIL_DESIGN), cardBackground: event.target.value } }))} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                        </div>
                                        <div className="space-y-1.5">
                                          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Header Background</label>
                                          <input type="text" value={selectedEmailTemplate.design?.headerBackground || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, design: { ...(current.design || DEFAULT_WRAPPED_EMAIL_DESIGN), headerBackground: event.target.value } }))} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                        </div>
                                        <div className="space-y-1.5">
                                          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Header Text Color</label>
                                          <input type="text" value={selectedEmailTemplate.design?.headerTextColor || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, design: { ...(current.design || DEFAULT_WRAPPED_EMAIL_DESIGN), headerTextColor: event.target.value } }))} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                        </div>
                                        <div className="space-y-1.5">
                                          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Body Text Color</label>
                                          <input type="text" value={selectedEmailTemplate.design?.bodyTextColor || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, design: { ...(current.design || DEFAULT_WRAPPED_EMAIL_DESIGN), bodyTextColor: event.target.value } }))} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                        </div>
                                        <div className="space-y-1.5">
                                          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Muted Text Color</label>
                                          <input type="text" value={selectedEmailTemplate.design?.mutedTextColor || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, design: { ...(current.design || DEFAULT_WRAPPED_EMAIL_DESIGN), mutedTextColor: event.target.value } }))} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                        </div>
                                        <div className="space-y-1.5">
                                          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Button Background</label>
                                          <input type="text" value={selectedEmailTemplate.design?.buttonBackground || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, design: { ...(current.design || DEFAULT_WRAPPED_EMAIL_DESIGN), buttonBackground: event.target.value } }))} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                        </div>
                                        <div className="space-y-1.5">
                                          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Button Text Color</label>
                                          <input type="text" value={selectedEmailTemplate.design?.buttonTextColor || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, design: { ...(current.design || DEFAULT_WRAPPED_EMAIL_DESIGN), buttonTextColor: event.target.value } }))} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                        </div>
                                        <div className="space-y-1.5">
                                          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Border Color</label>
                                          <input type="text" value={selectedEmailTemplate.design?.borderColor || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, design: { ...(current.design || DEFAULT_WRAPPED_EMAIL_DESIGN), borderColor: event.target.value } }))} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                        </div>
                                        <div className="space-y-1.5">
                                          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Border Radius</label>
                                          <input type="text" value={selectedEmailTemplate.design?.borderRadius || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, design: { ...(current.design || DEFAULT_WRAPPED_EMAIL_DESIGN), borderRadius: event.target.value } }))} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                        </div>
                                        <div className="space-y-1.5 md:col-span-2">
                                          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Font Family</label>
                                          <input type="text" value={selectedEmailTemplate.design?.fontFamily || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, design: { ...(current.design || DEFAULT_WRAPPED_EMAIL_DESIGN), fontFamily: event.target.value } }))} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                        </div>
                                      </div>
                                    </div>

                                    <div className="space-y-1.5">
                                      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Intro</label>
                                      <textarea value={selectedEmailTemplate.intro || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, intro: event.target.value }))} rows={3} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                    </div>

                                    <div className="space-y-1.5">
                                      <div className="flex items-center justify-between gap-3">
                                        <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">HTML Body</label>
                                        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50">
                                          <Upload className="h-4 w-4" />
                                          {isUploadingEmailTemplateImage ? 'Uploading image...' : 'Upload image'}
                                          <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleEmailTemplateImageUpload}
                                            disabled={isUploadingEmailTemplateImage}
                                            className="hidden"
                                          />
                                        </label>
                                      </div>
                                      <p className="text-xs text-gray-500">Upload an image to Firebase Storage, then paste the returned HTTPS URL into an <code>&lt;img&gt;</code> tag in this HTML body.</p>
                                      {emailTemplateImageStatus ? (
                                        <p className="text-xs text-gray-600">{emailTemplateImageStatus}</p>
                                      ) : null}
                                      {emailTemplateImageUrl ? (
                                        <input
                                          type="text"
                                          readOnly
                                          value={emailTemplateImageUrl}
                                          className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm font-mono text-gray-700"
                                        />
                                      ) : null}
                                      <textarea value={selectedEmailTemplate.bodyHtml || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, bodyHtml: event.target.value }))} rows={8} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                    </div>
                                  </>
                                )}

                                {selectedEmailTemplate.htmlMode === 'full' && (
                                  <div className="space-y-1.5">
                                    <div className="flex flex-wrap gap-2">
                                      <button type="button" onClick={() => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, fullHtml: '' }))} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50">
                                        Start From Scratch
                                      </button>
                                      <button type="button" onClick={() => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, fullHtml: buildWrappedEmailHtmlTemplate(current) }))} className="rounded-md border border-sigma-200 bg-sigma-50 px-3 py-2 text-xs font-medium text-sigma-800 hover:bg-sigma-100">
                                        Use Wrapped Layout As Base
                                      </button>
                                    </div>
                                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Full HTML</label>
                                    <textarea value={selectedEmailTemplate.fullHtml || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, fullHtml: event.target.value }))} rows={16} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                    <p className="text-xs text-gray-500">This replaces the entire email wrapper. Template variables still work in the HTML, and you can either begin with a blank canvas or generate HTML from the wrapped design above.</p>
                                  </div>
                                )}

                                <div className="space-y-1.5">
                                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Plain Text Body</label>
                                  <textarea value={selectedEmailTemplate.textBody || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, textBody: event.target.value }))} rows={6} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Action Label</label>
                                    <input type="text" value={selectedEmailTemplate.actionLabel || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, actionLabel: event.target.value }))} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                  </div>
                                  <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Action URL</label>
                                    <input type="text" value={selectedEmailTemplate.actionUrl || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, actionUrl: event.target.value }))} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                  </div>
                                </div>

                                <div className="space-y-1.5">
                                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Footer Text</label>
                                  <textarea value={selectedEmailTemplate.footerText || ''} onChange={(event) => updateEmailTemplate(selectedEmailTemplateKey, (current) => ({ ...current, footerText: event.target.value }))} rows={3} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300" />
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-3">
                          <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">Available Variables</div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {EMAIL_TEMPLATE_VARIABLES.map((variable) => (
                              <code key={variable} className="rounded bg-white px-2 py-1 text-xs text-gray-700 border border-gray-200">{variable}</code>
                            ))}
                          </div>
                        </div>

                        <div className="rounded-md border border-sigma-200 bg-sigma-50/50 p-4 space-y-4">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">Manual Send</h4>
                            <p className="text-xs text-gray-500 mt-1">Send an admin-configured user template either to an existing signed up user or to a comma-separated list of prospective users.</p>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => setManualEmailTargetMode('existing')}
                              className={['rounded-md px-3 py-2 text-xs font-medium border transition-colors', manualEmailTargetMode === 'existing' ? 'border-sigma-300 bg-sigma-100 text-sigma-900' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'].join(' ')}
                            >
                              Existing User
                            </button>
                            <button
                              type="button"
                              onClick={() => setManualEmailTargetMode('prospective')}
                              className={['rounded-md px-3 py-2 text-xs font-medium border transition-colors', manualEmailTargetMode === 'prospective' ? 'border-sigma-300 bg-sigma-100 text-sigma-900' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'].join(' ')}
                            >
                              Prospective Users
                            </button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Template</label>
                              <select value={manualEmailTemplateKey} onChange={(event) => setManualEmailTemplateKey(event.target.value)} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300">
                                {manualSendTemplateOptions.map((option) => (
                                  <option key={option.key} value={option.key}>{option.label}</option>
                                ))}
                              </select>
                            </div>
                            {manualEmailTargetMode === 'existing' ? (
                              <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Users</label>
                                <select
                                  multiple
                                  size={Math.min(Math.max(adminUsers.length, 3), 8)}
                                  value={manualEmailUserIds}
                                  onChange={(event) => {
                                    setManualEmailUserIds(Array.from(event.target.selectedOptions).map((option) => option.value));
                                  }}
                                  className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300"
                                >
                                  {adminUsers.map((entry) => (
                                    <option key={entry.id} value={entry.id}>
                                      {entry.displayName} {entry.email ? `(${entry.email})` : ''}
                                    </option>
                                  ))}
                                </select>
                                <p className="text-xs text-gray-500">{manualEmailUserIds.length} selected. Use Ctrl or Shift to select multiple users.</p>
                              </div>
                            ) : (
                              <div className="space-y-1.5">
                                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Prefill Variables From</label>
                                <select value={manualEmailPrefillUserId} onChange={(event) => setManualEmailPrefillUserId(event.target.value)} className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300">
                                  <option value="">No prefill user</option>
                                  {adminUsers.map((entry) => (
                                    <option key={entry.id} value={entry.id}>
                                      {entry.displayName} {entry.email ? `(${entry.email})` : ''}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                            <div className="space-y-1.5 md:col-span-2">
                              <div className="flex items-center justify-between gap-2">
                                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Document Attachment</label>
                                <button
                                  type="button"
                                  onClick={loadEmailAttachmentDocuments}
                                  disabled={isLoadingEmailAttachmentDocuments}
                                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-60"
                                >
                                  <RefreshCw className="h-3 w-3" />
                                  {isLoadingEmailAttachmentDocuments ? 'Loading' : 'Refresh'}
                                </button>
                              </div>
                              <select
                                value={manualEmailAttachmentDocumentId}
                                onChange={(event) => {
                                  setManualEmailAttachmentDocumentId(event.target.value);
                                  if (event.target.value) setManualEmailUploadedAttachment(null);
                                }}
                                className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-sigma-300"
                              >
                                <option value="">No attachment</option>
                                {manualEmailAttachmentDocuments.map((entry) => (
                                  <option key={entry.id} value={entry.id}>
                                    {entry.name || 'Untitled Document'} {entry.contentType === 'pdf' ? '(PDF)' : '(Document)'}
                                  </option>
                                ))}
                              </select>
                              <div className="flex flex-wrap items-center gap-2">
                                <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50">
                                  <Upload className="h-3.5 w-3.5" />
                                  {isUploadingManualEmailAttachment ? 'Uploading...' : 'Upload PDF'}
                                  <input
                                    type="file"
                                    accept="application/pdf"
                                    onChange={handleManualEmailAttachmentUpload}
                                    disabled={isUploadingManualEmailAttachment}
                                    className="hidden"
                                  />
                                </label>
                                {manualEmailUploadedAttachment ? (
                                  <button
                                    type="button"
                                    onClick={() => setManualEmailUploadedAttachment(null)}
                                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                                  >
                                    <X className="h-3 w-3" />
                                    Clear {manualEmailUploadedAttachment.name}
                                  </button>
                                ) : null}
                              </div>
                              <p className="text-xs text-gray-500">Choose an existing document or upload a PDF. Large PDFs over 15 MB are blocked.</p>
                            </div>
                          </div>

                          {manualEmailTargetMode === 'prospective' ? (
                            <div className="space-y-1.5">
                              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Prospective Emails</label>
                              <textarea
                                value={manualProspectiveEmails}
                                onChange={(event) => setManualProspectiveEmails(event.target.value)}
                                rows={4}
                                placeholder="lead1@example.com, lead2@example.com"
                                className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sigma-300"
                              />
                              <p className="text-xs text-gray-500">Enter one or more email addresses separated by commas or new lines. Optional prefill lets the template reuse values from an existing user row.</p>
                            </div>
                          ) : null}

                          <div className="flex flex-wrap items-center gap-3">
                            <button
                              type="button"
                              onClick={handleManualEmailSend}
                              disabled={isManualEmailSending || !manualEmailTemplateKey || (manualEmailTargetMode === 'existing' ? manualEmailUserIds.length === 0 : !manualProspectiveEmails.trim())}
                              className="inline-flex items-center gap-2 rounded-md bg-sigma-700 px-4 py-2 text-sm font-medium text-white hover:bg-sigma-800 disabled:opacity-60"
                            >
                              {isManualEmailSending ? 'Sending...' : 'Send Email'}
                            </button>
                            {manualEmailTargetMode === 'existing' && manualEmailUsers.length > 0 ? <div className="text-xs text-gray-500">Recipients: {manualEmailUsers.length === 1 ? `${manualEmailUsers[0].displayName}${manualEmailUsers[0].email ? ` (${manualEmailUsers[0].email})` : ''}` : `${manualEmailUsers.length} selected users`}</div> : null}
                            {manualEmailTargetMode === 'prospective' ? <div className="text-xs text-gray-500">Recipients: comma-separated prospective emails. {manualPrefillUser ? `Prefill: ${manualPrefillUser.displayName}${manualPrefillUser.email ? ` (${manualPrefillUser.email})` : ''}.` : 'No prefill row selected.'}</div> : null}
                          </div>

                          {manualEmailStatus ? (
                            <div className={['rounded-md px-3 py-2 text-xs border', manualEmailStatus.toLowerCase().includes('sent ') ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'].join(' ')}>
                              {manualEmailStatus}
                            </div>
                          ) : null}
                        </div>
                      </section>
                      )}

                      {emailMainSection === 'settings' && (
                      <section className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                        <h3 className="text-sm font-semibold text-amber-900">Required Firebase Secrets</h3>
                        <p className="text-xs text-amber-800 mt-1">
                          {emailProvider === 'gmail'
                            ? 'Gmail provider uses `EMAIL_USER` and `EMAIL_PASSWORD`.'
                            : 'ZeptoMail provider uses `ZEPTOMAIL_AUTH_TOKEN`, `ZEPTOMAIL_FROM_EMAIL`, and `ZEPTOMAIL_FROM_NAME`.'}
                        </p>
                      </section>
                      )}
                    </>
                  )}
                </div>
              )}

              {error && <div className="mt-4 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}
              {success && <div className="mt-4 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">{success}</div>}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
